import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { createCompetitorReport } from '../../api_handlers/competitor';
import { getBrandList } from '../../api_handlers/brand';
import { Typography, Button, Card, CardContent, CardHeader, Box, TextField, IconButton } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import {
  Assessment,
  ArrowBack,
  Add,
  Delete,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },
  card: {
    marginBottom: theme.spacing(2),
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  cardHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: theme.shape.borderRadius,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    color: 'white',
    fontSize: 24,
  },
  formSection: {
    marginBottom: theme.spacing(3),
  },
  formLabel: {
    fontSize: '0.875rem',
    fontWeight: 500,
    marginBottom: theme.spacing(1),
    display: 'block',
  },
  formControl: {
    marginBottom: theme.spacing(2),
    minWidth: '100%',
  },
  platformButtons: {
    display: 'flex',
    marginTop: theme.spacing(1),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(2),
    },
  },
  platformButton: {
    flex: 1,
  },
  dateGrid: {
    display: 'flex',
    marginTop: theme.spacing(1),
    '& > *': {
      flex: 1,
      '&:not(:first-child)': {
        marginLeft: theme.spacing(2),
      },
    },
  },
  dateInput: {
    marginBottom: theme.spacing(2),
  },
  dateLabel: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
  },
  quickDateButtons: {
    display: 'flex',
    marginTop: theme.spacing(1),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  competitorInput: {
    marginBottom: theme.spacing(1),
  },
  competitorRow: {
    display: 'flex',
    marginBottom: theme.spacing(1),
    '& > *:not(:first-child)': {
      marginLeft: theme.spacing(1),
    },
  },
  helperText: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
}));

export default function CreateReport() {
  const classes = useStyles();
  const history = useHistory();

  // Form state
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [platform, setPlatform] = useState('both');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [title, setTitle] = useState('');
  const [competitorHandles, setCompetitorHandles] = useState(['']);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      setLoadingBrands(true);
      const response = await getBrandList();
      if (Array.isArray(response)) {
        setBrands(response);
        if (response.length > 0) {
          setSelectedBrand(response[0]);
        }
      }
    } catch (err) {
      console.error('Error loading brands:', err);
      setError('Failed to load brands. Please refresh the page.');
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleAddCompetitor = () => {
    if (competitorHandles.length < 5) {
      setCompetitorHandles([...competitorHandles, '']);
    }
  };

  const handleRemoveCompetitor = (index) => {
    const newHandles = competitorHandles.filter((_, i) => i !== index);
    setCompetitorHandles(newHandles);
  };

  const handleCompetitorChange = (index, value) => {
    const newHandles = [...competitorHandles];
    newHandles[index] = value;
    setCompetitorHandles(newHandles);
  };

  const handleQuickDateSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    setEndDate(end.toISOString().split('T')[0]);
    setStartDate(start.toISOString().split('T')[0]);
  };

  const handleGenerateReport = async () => {
    // Validation
    if (!selectedBrand) {
      setError('Please select a brand');
      return;
    }
    if (competitorHandles.filter((h) => h.trim()).length === 0) {
      setError('Please add at least one competitor handle');
      return;
    }
    if (platform === 'both' && !startDate && !endDate) {
      // Optional for now, but could be required
    }

    try {
      setIsLoading(true);
      setError(null);

      const payload = {
        brand: selectedBrand.id,
        platform: platform.toLowerCase(),
        competitor_handles: competitorHandles
          .filter((handle) => handle.trim())
          .map((handle) => (handle.startsWith('@') ? handle : `@${handle}`)),
      };

      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;
      if (title.trim()) payload.title = title.trim();

      const response = await createCompetitorReport(payload);

      if (response && (response.results || response.created_count > 0)) {
        // Success - navigate to reports page
        history.push('/competitor-report');
      } else {
        setError(response?.errors?.join(', ') || 'Failed to create report. Please try again.');
      }
    } catch (err) {
      console.error('Error creating report:', err);
      setError(err.message || 'Failed to create report. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = () => {
    history.push('/competitor-report');
  };

  return (
    <div className={classes.root}>
      <Box className={classes.headerContainer}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBack />}
          onClick={handleBack}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" component="h1" style={{ fontWeight: 700 }}>
            Create Competitor Report
          </Typography>
          <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
            Configure your custom competitor analysis
          </Typography>
        </Box>
      </Box>

      <Card className={classes.card}>
        <CardHeader
          title={
            <Box className={classes.cardHeaderContent}>
              <div className={classes.iconContainer}>
                <Assessment className={classes.icon} />
              </div>
              <Box>
                <Typography variant="h6" style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                  Report Configuration
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Set parameters for competitor analysis
                </Typography>
              </Box>
            </Box>
          }
        />
        <CardContent>
          {error && (
            <Box marginBottom={2} padding={2} style={{ backgroundColor: '#fee2e2', borderRadius: 4 }}>
              <Typography variant="body2" color="error">
                {error}
              </Typography>
            </Box>
          )}

          {/* Your Brand Selection */}
          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>Your Brand</Typography>
            <Autocomplete
              options={brands}
              getOptionLabel={(option) => (option ? option.name : '')}
              value={selectedBrand}
              onChange={(event, newValue) => {
                setSelectedBrand(newValue);
              }}
              loading={loadingBrands}
              disabled={loadingBrands}
              noOptionsText="No brands found"
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search or select your brand"
                  fullWidth
                />
              )}
            />
          </Box>

          {/* Report Title (Optional) */}
          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>Report Title (Optional)</Typography>
            <TextField
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Coffee Brands Q1 2024 Analysis"
              fullWidth
              variant="outlined"
            />
          </Box>

          {/* Platform Selection */}
          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>Platform</Typography>
            <Box className={classes.platformButtons}>
              <Button
                variant={platform === 'Instagram' ? 'contained' : 'outlined'}
                onClick={() => setPlatform('Instagram')}
                className={classes.platformButton}
              >
                Instagram
              </Button>
              <Button
                variant={platform === 'YouTube' ? 'contained' : 'outlined'}
                onClick={() => setPlatform('YouTube')}
                className={classes.platformButton}
              >
                YouTube
              </Button>
              <Button
                variant={platform === 'both' ? 'contained' : 'outlined'}
                onClick={() => setPlatform('both')}
                className={classes.platformButton}
              >
                Both
              </Button>
            </Box>
          </Box>

          {/* Date Range */}
          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>Duration</Typography>
            <Box className={classes.dateGrid}>
              <Box>
                <Typography className={classes.dateLabel}>Start Date</Typography>
                <TextField
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  variant="outlined"
                />
              </Box>
              <Box>
                <Typography className={classes.dateLabel}>End Date</Typography>
                <TextField
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  variant="outlined"
                />
              </Box>
            </Box>

            {/* Quick Select Buttons */}
            <Box className={classes.quickDateButtons}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleQuickDateSelect(7)}
              >
                Last 7 Days
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleQuickDateSelect(30)}
              >
                Last 30 Days
              </Button>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleQuickDateSelect(90)}
              >
                Last 90 Days
              </Button>
            </Box>
          </Box>

          {/* Competitor Handles */}
          <Box className={classes.formSection}>
            <Typography className={classes.formLabel}>Competitor Brand Handles</Typography>
            <Typography className={classes.helperText}>
              Add up to 5 competitor handles to compare
            </Typography>
            {competitorHandles.map((handle, index) => (
              <Box key={index} className={classes.competitorRow}>
                <TextField
                  placeholder={`@competitor${index + 1}`}
                  value={handle}
                  onChange={(e) => handleCompetitorChange(index, e.target.value)}
                  fullWidth
                  variant="outlined"
                />
                {competitorHandles.length > 1 && (
                  <IconButton
                    onClick={() => handleRemoveCompetitor(index)}
                    color="secondary"
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                )}
              </Box>
            ))}

            {competitorHandles.length < 5 && (
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleAddCompetitor}
                fullWidth
                style={{ marginTop: 8 }}
              >
                Add Competitor
              </Button>
            )}
          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleGenerateReport}
            disabled={isLoading}
            startIcon={<Assessment />}
            style={{ marginTop: 24 }}
          >
            {isLoading ? 'Generating Report...' : 'Generate Report'}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

